// src/services/hrService.ts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import {
  Employee,
  Attendance,
  Leave,
  Payroll,
  EmployeePermissions,
} from '../types/firestore';

// ÙˆØ§Ø¬Ù‡Ø© Ù„Ù„Ù…ÙˆØ¸Ù Ø§Ù„Ù…Ø¹Ù„Ù‚ Ù„Ù„ØªÙØ¹ÙŠÙ„
export interface PendingEmployee extends Omit<Employee, 'id'> {
  id?: string;
  pendingActivation?: {
    invitationSent?: boolean;
    createdBy: string;
    createdAt: any;
  };
}

type EmployeeOwnership = {
  createdBy?: string;
  teacherId?: string;
  tenantId?: string;
};

type AttendanceWrite = Omit<Attendance, 'id' | 'createdAt' | 'updatedAt'> &
  EmployeeOwnership;

type PayrollWrite = Omit<Payroll, 'id' | 'createdAt' | 'updatedAt'> &
  EmployeeOwnership;

type EmployeeAccountFunctionResult = {
  employeeId?: string;
  uid?: string;
  passwordSetupLink?: string;
};

type EmployeeAccessFunctionResult = {
  success: boolean;
};

// Ø¯Ø§Ù„Ø© Ù…Ø³Ø§Ø¹Ø¯Ø© Ù„Ø¥Ø²Ø§Ù„Ø© Ø§Ù„Ù‚ÙŠÙ… undefined Ù…Ù† Ø§Ù„ÙƒØ§Ø¦Ù†
function removeUndefinedFields(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(removeUndefinedFields);
  }

  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefinedFields(value);
      }
    }
    return cleaned;
  }

  return obj;
}

function getEmployeeOwner(employee: Employee | null): string | undefined {
  if (!employee) return undefined;
  const record = employee as Employee & EmployeeOwnership;
  return record.createdBy || record.teacherId || record.tenantId;
}

function withOwnerFields(ownerId?: string): EmployeeOwnership {
  return ownerId
    ? {
        createdBy: ownerId,
        teacherId: ownerId,
        tenantId: ownerId,
      }
    : {};
}

function normalizeEmail(email: string) {
  return email.trim().toLocaleLowerCase();
}

// ======================== EMPLOYEES SERVICE ========================
class EmployeesService {
  private collectionName = 'employees';

  private validateEmployeeInput(
    employeeData: Pick<
      Employee,
      | 'email'
      | 'name'
      | 'employeeId'
      | 'department'
      | 'role'
      | 'position'
      | 'salary'
      | 'employment'
    >
  ) {
    if (!employeeData.name?.trim()) throw new Error('اسم الموظف مطلوب');
    if (!employeeData.email?.trim()) throw new Error('البريد الإلكتروني مطلوب');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(employeeData.email)) {
      throw new Error('البريد الإلكتروني غير صحيح');
    }
    if (!employeeData.employeeId?.trim()) throw new Error('رقم الموظف مطلوب');
    if (!employeeData.department) throw new Error('القسم مطلوب');
    if (!employeeData.role) throw new Error('الدور مطلوب');
    if (!employeeData.position?.trim()) throw new Error('المسمى الوظيفي مطلوب');
    if (!employeeData.salary || employeeData.salary.base <= 0) {
      throw new Error('الراتب الأساسي يجب أن يكون أكبر من صفر');
    }
    if (employeeData.salary.currency !== 'EGP') {
      throw new Error('عملة الرواتب المعتمدة هي EGP فقط');
    }
    if (!employeeData.employment?.hireDate) {
      throw new Error('تاريخ التعيين مطلوب');
    }
  }

  private async ensureUniqueEmployee(
    creatorId: string,
    employeeData: Pick<Employee, 'email' | 'employeeId'>,
    currentEmployeeId?: string
  ) {
    const [emailSnapshot, codeSnapshot] = await Promise.all([
      getDocs(
        query(
          collection(db, this.collectionName),
          where('createdBy', '==', creatorId),
          where('email', '==', normalizeEmail(employeeData.email)),
          limit(1)
        )
      ),
      getDocs(
        query(
          collection(db, this.collectionName),
          where('createdBy', '==', creatorId),
          where('employeeId', '==', employeeData.employeeId.trim()),
          limit(1)
        )
      ),
    ]);

    const emailExists = emailSnapshot.docs.some(
      item => item.id !== currentEmployeeId
    );
    const codeExists = codeSnapshot.docs.some(
      item => item.id !== currentEmployeeId
    );

    if (emailExists) {
      throw new Error('البريد الإلكتروني مستخدم بالفعل داخل المؤسسة');
    }
    if (codeExists) {
      throw new Error('رقم الموظف مستخدم بالفعل داخل المؤسسة');
    }
  }

  // Ø¥Ù†Ø´Ø§Ø¡ Ù…ÙˆØ¸Ù Ø¬Ø¯ÙŠØ¯ Ù…Ø¹ Ø­Ø³Ø§Ø¨ Firebase Auth
  private serializeEmployeeForFunction(employeeData: any) {
    const toIso = (value: any): string | undefined => {
      if (!value) return undefined;
      if (typeof value === 'string') return value;
      if (value instanceof Date) return value.toISOString();
      if (typeof value.toDate === 'function') return value.toDate().toISOString();
      return undefined;
    };

    return removeUndefinedFields({
      ...employeeData,
      email: normalizeEmail(employeeData.email),
      password: undefined,
      personalInfo: {
        ...employeeData.personalInfo,
        dateOfBirth: toIso(employeeData.personalInfo?.dateOfBirth),
      },
      employment: {
        ...employeeData.employment,
        hireDate: toIso(employeeData.employment?.hireDate),
        probationEndDate: toIso(employeeData.employment?.probationEndDate),
        contractEndDate: toIso(employeeData.employment?.contractEndDate),
      },
    });
  }

  async createEmployeeWithAuth(
    employeeData: {
      email: string;
      password?: string;
      name: string;
      phone: string;
      employeeId: string;
      role: Employee['role'];
      department: Employee['department'];
      position: string;
      salary: Employee['salary'];
      personalInfo: Employee['personalInfo'];
      employment: Employee['employment'];
      permissions: EmployeePermissions;
    },
    creatorId: string
  ): Promise<string> {
    try {
      // Ø§Ù„ØªØ­Ù‚Ù‚ Ù…Ù† ØµÙ„Ø§Ø­ÙŠØ© Ø§Ù„Ù…Ø¯Ø±Ø³ Ù„Ø¥Ù†Ø´Ø§Ø¡ Ø§Ù„Ù…ÙˆØ¸ÙÙŠÙ†
      this.validateEmployeeInput(employeeData);
      await this.ensureUniqueEmployee(creatorId, employeeData);

      const canCreateWithFunction = await this.canCreateEmployees(creatorId);
      if (!canCreateWithFunction) {
        throw new Error('غير مصرح لك بإنشاء حسابات الموظفين');
      }

      const createAccount = httpsCallable<
        { teacherId: string; employee: any },
        EmployeeAccountFunctionResult
      >(functions, 'createEmployeeAccount');
      const result = await createAccount({
        teacherId: creatorId,
        employee: this.serializeEmployeeForFunction(employeeData),
      });

      if (!result.data.employeeId) {
        throw new Error('فشل في إنشاء حساب الموظف');
      }

      return result.data.employeeId;

    } catch (error) {
      console.error('Error creating employee with auth:', error);
      throw error;
    }
  }

  // Ø¥Ù†Ø´Ø§Ø¡ Ù…ÙˆØ¸Ù Ø¬Ø¯ÙŠØ¯ (Ø§Ù„Ø·Ø±ÙŠÙ‚Ø© Ø§Ù„Ø£ØµÙ„ÙŠØ©)
  async createEmployee(
    employeeData: Omit<Employee, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    try {
      const ownerId = (employeeData as Employee & EmployeeOwnership).createdBy;
      if (!ownerId) throw new Error('معرف مالك الموظف مطلوب');
      this.validateEmployeeInput(employeeData);
      await this.ensureUniqueEmployee(ownerId, employeeData);

      const now = Timestamp.now();
      const docData = removeUndefinedFields({
        ...employeeData,
        email: normalizeEmail(employeeData.email),
        ...withOwnerFields(ownerId),
        createdAt: now,
        updatedAt: now,
      });

      const docRef = await addDoc(collection(db, this.collectionName), docData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating employee:', error);
      throw error;
    }
  }

  // Ø¬Ù„Ø¨ Ø¬Ù…ÙŠØ¹ Ø§Ù„Ù…ÙˆØ¸ÙÙŠÙ†
  async getAllEmployees(): Promise<Employee[]> {
    try {
      const q = query(collection(db, this.collectionName), orderBy('name'));

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        doc =>
          ({
            id: doc.id,
            ...doc.data(),
          } as Employee)
      );
    } catch (error) {
      console.error('Error getting employees:', error);
      throw error;
    }
  }

  // Ø¬Ù„Ø¨ Ø§Ù„Ù…ÙˆØ¸ÙÙŠÙ† Ø§Ù„Ø®Ø§ØµÙŠÙ† Ø¨Ù…Ø¯Ø±Ø³/Ù…Ù†Ø´Ø¦ Ù…Ø­Ø¯Ø¯ ÙÙ‚Ø·
  async getByCreatedBy(createdBy: string): Promise<Employee[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('createdBy', '==', createdBy)
      );

      const querySnapshot = await getDocs(q);
      const employees = querySnapshot.docs.map(
        doc =>
          ({
            id: doc.id,
            ...doc.data(),
          } as Employee)
      );
      // ØªØ±ØªÙŠØ¨ ÙÙŠ Ø§Ù„Ø°Ø§ÙƒØ±Ø© Ù„ØªØ¬Ù†Ø¨ Ø§Ù„Ø­Ø§Ø¬Ø© Ù„Ù€ composite index
      return employees.sort((a, b) => a.name.localeCompare(b.name, 'ar'));
    } catch (error) {
      console.error('Error getting employees by createdBy:', error);
      throw error;
    }
  }

  // Ø§Ù„Ø¨Ø­Ø« Ø¹Ù† Ù…ÙˆØ¸Ù Ø¨Ù€ UID
  async getTeamMembers(
    ownerId: string,
    department: Employee['department'],
    excludeEmployeeId?: string
  ): Promise<Employee[]> {
    try {
      if (!ownerId) return [];
      const q = query(
        collection(db, this.collectionName),
        where('createdBy', '==', ownerId),
        where('department', '==', department),
        where('isActive', '==', true)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs
        .map(
          doc =>
            ({
              id: doc.id,
              ...doc.data(),
            } as Employee)
        )
        .filter(member => member.id !== excludeEmployeeId)
        .sort((a, b) => a.name.localeCompare(b.name, 'ar'));
    } catch (error) {
      console.error('Error getting employee team members:', error);
      throw error;
    }
  }

  async getEmployeeByUid(uid: string): Promise<Employee | null> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('uid', '==', uid),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data(),
      } as Employee;
    } catch (error) {
      console.error('Error getting employee by UID:', error);
      throw error;
    }
  }

  // Ø¬Ù„Ø¨ Ø§Ù„Ù…ÙˆØ¸ÙÙŠÙ† Ø§Ù„Ù†Ø´Ø·ÙŠÙ†
  async getActiveEmployees(): Promise<Employee[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('isActive', '==', true),
        orderBy('name')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        doc =>
          ({
            id: doc.id,
            ...doc.data(),
          } as Employee)
      );
    } catch (error) {
      console.error('Error getting active employees:', error);
      throw error;
    }
  }

  // Ø¬Ù„Ø¨ Ø§Ù„Ù…ÙˆØ¸ÙÙŠÙ† Ø­Ø³Ø¨ Ø§Ù„Ø¯ÙˆØ±
  async getEmployeesByRole(role: Employee['role']): Promise<Employee[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('role', '==', role),
        where('isActive', '==', true),
        orderBy('name')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        doc =>
          ({
            id: doc.id,
            ...doc.data(),
          } as Employee)
      );
    } catch (error) {
      console.error('Error getting employees by role:', error);
      throw error;
    }
  }

  // Ø¬Ù„Ø¨ Ù…ÙˆØ¸Ù Ù…Ø­Ø¯Ø¯
  async getEmployeeById(employeeId: string): Promise<Employee | null> {
    try {
      const docRef = doc(db, this.collectionName, employeeId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
        } as Employee;
      }
      return null;
    } catch (error) {
      console.error('Error getting employee:', error);
      throw error;
    }
  }

  // ØªØ­Ø¯ÙŠØ« Ù…ÙˆØ¸Ù
  async updateEmployee(
    employeeId: string,
    updateData: Partial<Employee>
  ): Promise<void> {
    try {
      const existing = await this.getEmployeeById(employeeId);
      const ownerId = getEmployeeOwner(existing);
      if (!existing || !ownerId) throw new Error('الموظف غير موجود');

      if (updateData.email || updateData.employeeId) {
        await this.ensureUniqueEmployee(
          ownerId,
          {
            email: updateData.email || existing.email,
            employeeId: updateData.employeeId || existing.employeeId,
          },
          employeeId
        );
      }

      const accessChanged =
        updateData.role !== undefined ||
        updateData.department !== undefined ||
        updateData.position !== undefined ||
        updateData.permissions !== undefined ||
        updateData.salary !== undefined;

      if (accessChanged) {
        const updateAccess = httpsCallable<
          {
            employeeId: string;
            role: Employee['role'];
            department: Employee['department'];
            position: string;
            permissions: EmployeePermissions;
            salary?: Employee['salary'];
          },
          EmployeeAccessFunctionResult
        >(functions, 'updateEmployeeAccess');
        await updateAccess({
          employeeId,
          role: updateData.role || existing.role,
          department: updateData.department || existing.department,
          position: updateData.position || existing.position,
          permissions: updateData.permissions || existing.permissions,
          salary: updateData.salary || existing.salary,
        });
      }

      const docRef = doc(db, this.collectionName, employeeId);
      const {
        role,
        department,
        position,
        permissions,
        salary,
        accountStatus,
        uid: ignoredUid,
        isActive,
        ...safeUpdateData
      } = updateData as Partial<Employee> & { uid?: string };
      void role;
      void department;
      void position;
      void permissions;
      void salary;
      void accountStatus;
      void ignoredUid;
      void isActive;
      const cleanUpdateData = removeUndefinedFields({
        ...safeUpdateData,
        email: updateData.email ? normalizeEmail(updateData.email) : undefined,
        createdBy: undefined,
        teacherId: undefined,
        tenantId: undefined,
        updatedAt: Timestamp.now(),
      });
      const fieldsToUpdate = Object.keys(cleanUpdateData).filter(
        key => cleanUpdateData[key] !== undefined
      );
      if (fieldsToUpdate.length > 1 || !accessChanged) {
        await updateDoc(docRef, cleanUpdateData);
      }
    } catch (error) {
      console.error('Error updating employee:', error);
      throw error;
    }
  }

  // Ø­Ø°Ù Ù…ÙˆØ¸Ù (Ø¥Ù„ØºØ§Ø¡ ØªÙØ¹ÙŠÙ„)
  async deactivateEmployee(
    employeeId: string,
    lastWorkingDay?: Date
  ): Promise<void> {
    try {
      void lastWorkingDay;
      const disableAccount = httpsCallable<
        { employeeId: string },
        { success: boolean }
      >(functions, 'disableEmployeeAccount');
      await disableAccount({ employeeId });
      return;
    } catch (error) {
      console.error('Error deactivating employee:', error);
      throw error;
    }
  }

  // ØªØ­Ø¯ÙŠØ« ØµÙ„Ø§Ø­ÙŠØ§Øª Ø§Ù„Ù…ÙˆØ¸Ù
  async updateEmployeePermissions(
    employeeId: string,
    permissions: EmployeePermissions
  ): Promise<void> {
    try {
      const existing = await this.getEmployeeById(employeeId);
      if (!existing) throw new Error('الموظف غير موجود');
      const updateAccess = httpsCallable<
        {
          employeeId: string;
          role: Employee['role'];
          department: Employee['department'];
          position: string;
          permissions: EmployeePermissions;
          salary?: Employee['salary'];
        },
        EmployeeAccessFunctionResult
      >(functions, 'updateEmployeeAccess');
      await updateAccess({
        employeeId,
        role: existing.role,
        department: existing.department,
        position: existing.position,
        permissions,
        salary: existing.salary,
      });
    } catch (error) {
      console.error('Error updating employee permissions:', error);
      throw error;
    }
  }

  async completeEmployeeOnboarding(employeeId: string): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, employeeId);
      await updateDoc(docRef, {
        accountStatus: 'active',
        onboardingCompleted: true,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error completing employee onboarding:', error);
      throw error;
    }
  }

  // ØªÙØ¹ÙŠÙ„ Ø­Ø³Ø§Ø¨ Ø§Ù„Ù…ÙˆØ¸Ù Ø§Ù„Ù…Ø¹Ù„Ù‚
  async activateEmployee(employeeId: string, adminId: string): Promise<void> {
    try {
      void adminId;
      const activateAccount = httpsCallable<
        { employeeId: string },
        EmployeeAccountFunctionResult
      >(functions, 'activateEmployeeAccount');
      await activateAccount({ employeeId });
      return;

    } catch (error) {
      console.error('Error activating employee:', error);
      throw error;
    }
  }

  // Ø¬Ù„Ø¨ Ø§Ù„Ù…ÙˆØ¸ÙÙŠÙ† Ø§Ù„Ù…Ø¹Ù„Ù‚ÙŠÙ† Ù„Ù„ØªÙØ¹ÙŠÙ„
  async getPendingEmployees(ownerId: string): Promise<Employee[]> {
    try {
      if (!ownerId) return [];

      const q = query(
        collection(db, this.collectionName),
        where('createdBy', '==', ownerId)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs
        .map(doc =>
          ({
            id: doc.id,
            ...doc.data(),
          } as Employee)
        )
        .filter(employee => employee.accountStatus === 'invited')
        .sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });
    } catch (error) {
      console.error('Error getting pending employees:', error);
      throw error;
    }
  }

  // Ø§Ù„ØªØ­Ù‚Ù‚ Ù…Ù† ØµÙ„Ø§Ø­ÙŠØ© Ø¥Ù†Ø´Ø§Ø¡ Ø§Ù„Ù…ÙˆØ¸ÙÙŠÙ†
  async canCreateEmployees(userId: string): Promise<boolean> {
    try {
      console.log('Checking permissions for user:', userId);

      // Ø§Ù„ØªØ­Ù‚Ù‚ Ù…Ù† ÙˆØ¬ÙˆØ¯ userId
      if (!userId || userId.trim() === '') {
        console.log('User ID is empty or undefined');
        return false;
      }

      // Ø£ÙˆÙ„Ø§Ù‹ØŒ Ø§Ù„ØªØ­Ù‚Ù‚ Ø¥Ø°Ø§ ÙƒØ§Ù† Ø§Ù„Ù…Ø³ØªØ®Ø¯Ù… Ù…Ø¯Ø±Ø³Ø§Ù‹ Ø¨Ø§Ø³ØªØ®Ø¯Ø§Ù… Ø¹Ø¯Ø© Ø·Ø±Ù‚ Ù„Ù„Ø¨Ø­Ø«
      const teachersRef = collection(db, 'teachers');

      // Ø§Ù„Ø¨Ø­Ø« Ø¨Ù€ uid (Ù…Ø¹ Ø§Ù„ØªØ­Ù‚Ù‚ Ù…Ù† undefined)
      let teacherQuery = query(teachersRef, where('uid', '==', userId));
      let teacherSnapshot = await getDocs(teacherQuery);

      console.log(
        'Teacher query by uid result:',
        teacherSnapshot.size,
        'documents found'
      );

      // Ø¥Ø°Ø§ Ù„Ù… Ù†Ø¬Ø¯ Ø¨Ù€ uidØŒ Ù†Ø¨Ø­Ø« Ø¨Ù€ id
      if (teacherSnapshot.empty) {
        console.log('No teacher found by uid, trying id field...');
        teacherQuery = query(teachersRef, where('id', '==', userId));
        teacherSnapshot = await getDocs(teacherQuery);
        console.log(
          'Teacher query by id result:',
          teacherSnapshot.size,
          'documents found'
        );
      }

      // Ø¥Ø°Ø§ Ù„Ù… Ù†Ø¬Ø¯ Ø¨Ù€ idØŒ Ù†Ø¨Ø­Ø« ÙÙŠ document Ø¨Ù†ÙØ³ Ø§Ù„Ù…Ø¹Ø±Ù
      if (teacherSnapshot.empty) {
        console.log('No teacher found by id, checking document directly...');
        try {
          const teacherDoc = await getDoc(doc(db, 'teachers', userId));
          if (teacherDoc.exists()) {
            const teacherData = teacherDoc.data();
            console.log('Teacher data found directly:', {
              role: teacherData.role,
              name: teacherData.name,
            });

            // ÙˆØ¬ÙˆØ¯ Ø§Ù„Ù…Ø³ØªÙ†Ø¯ ÙÙŠ Ù…Ø¬Ù…ÙˆØ¹Ø© teachers ÙŠØ¹Ù†ÙŠ Ø£Ù† Ø§Ù„Ù…Ø³ØªØ®Ø¯Ù… Ù…Ø¯Ø±Ø³ â€” ÙŠÙØ³Ù…Ø­ Ù„Ù‡ Ø¨Ø¥Ù†Ø´Ø§Ø¡ Ù…ÙˆØ¸ÙÙŠÙ†
            console.log('Teacher can create employees: true (found in teachers collection)');
            return true;
          }
        } catch (error) {
          console.log('Error checking teacher document directly:', error);
        }
      }

      // Ø¥Ø°Ø§ ÙˆØ¬Ø¯Ù†Ø§ Ø§Ù„Ù…Ø¯Ø±Ø³ Ù…Ù† Ø§Ù„Ø§Ø³ØªØ¹Ù„Ø§Ù…
      if (!teacherSnapshot.empty) {
        const teacherData = teacherSnapshot.docs[0].data();
        console.log('Teacher data found:', {
          role: teacherData.role,
          name: teacherData.name,
        });

        // Ø§Ù„ØªØ­Ù‚Ù‚ Ù…Ù† Ø§Ù„Ø¯ÙˆØ± - Ø§Ù„Ù…Ø¯Ø±Ø³ÙˆÙ† ÙˆØ§Ù„Ù…Ø¯Ø±Ø§Ø¡ ÙˆØ§Ù„Ù…Ø´Ø±ÙÙŠÙ† ÙˆØ®Ø¯Ù…Ø© Ø§Ù„Ø¹Ù…Ù„Ø§Ø¡ ÙŠÙ…ÙƒÙ†Ù‡Ù… Ø¥Ù†Ø´Ø§Ø¡ Ù…ÙˆØ¸ÙÙŠÙ†
        const canCreate =
          teacherData.role === 'teacher' ||
          teacherData.role === 'admin' ||
          teacherData.role === 'supervisor' ||
          teacherData.role === 'customer_service';
        console.log('Teacher can create employees:', canCreate);
        return canCreate;
      }

      // Ø¥Ø°Ø§ Ù„Ù… ÙŠÙƒÙ† Ù…Ø¯Ø±Ø³Ø§Ù‹ØŒ Ø§Ù„ØªØ­Ù‚Ù‚ Ø¥Ø°Ø§ ÙƒØ§Ù† Ù…ÙˆØ¸ÙØ§Ù‹
      console.log(
        'User not found in teachers collection, checking employees...'
      );

      // Ø§Ù„ØªØ­Ù‚Ù‚ Ù…Ù† Ø¨ÙŠØ§Ù†Ø§Øª Ø§Ù„Ù…Ø³ØªØ®Ø¯Ù… ÙÙŠ Ù…Ø¬Ù…ÙˆØ¹Ø© users
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('User data found:', {
            role: userData.role,
            name: userData.name,
          });

          // Ø§Ù„ØªØ­Ù‚Ù‚ Ù…Ù† Ø§Ù„Ø¯ÙˆØ± - Ø¬Ù…ÙŠØ¹ Ø§Ù„Ø£Ø¯ÙˆØ§Ø± Ø§Ù„Ø¥Ø¯Ø§Ø±ÙŠØ© ÙŠÙ…ÙƒÙ†Ù‡Ø§ Ø¥Ù†Ø´Ø§Ø¡ Ù…ÙˆØ¸ÙÙŠÙ†
          const canCreate =
            userData.role === 'admin' ||
            userData.role === 'supervisor' ||
            userData.role === 'teacher' ||
            userData.role === 'customer_service' ||
            userData.role === 'hr_manager' ||
            userData.role === 'accountant' ||
            userData.role === 'assistant';
          console.log('User can create employees:', canCreate);
          return canCreate;
        }
      } catch (error) {
        console.log('Error checking user document:', error);
      }

      const employee = await this.getEmployeeById(userId);
      console.log(
        'Employee data:',
        employee ? { role: employee.role, name: employee.name } : 'not found'
      );

      const canCreateAsEmployee =
        employee?.role === 'admin' ||
        employee?.role === 'supervisor' ||
        employee?.role === 'hr_manager' ||
        employee?.role === 'accountant' ||
        employee?.role === 'customer_service' ||
        employee?.role === 'assistant';
      console.log('Employee can create:', canCreateAsEmployee);

      return canCreateAsEmployee;
    } catch (error) {
      console.error('Error checking create permission:', error);
      return false;
    }
  }
}

// ======================== ATTENDANCE SERVICE ========================
class AttendanceService {
  private collectionName = 'attendance';

  // ØªØ³Ø¬ÙŠÙ„ Ø­Ø¶ÙˆØ±
  async checkIn(
    employeeId: string,
    location?: { latitude: number; longitude: number; address?: string }
  ): Promise<string> {
    try {
      const employee = await employeesService.getEmployeeById(employeeId);
      const ownerId = getEmployeeOwner(employee);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Ø§Ù„ØªØ­Ù‚Ù‚ Ù…Ù† ÙˆØ¬ÙˆØ¯ Ø³Ø¬Ù„ Ø­Ø¶ÙˆØ± Ù„Ù„ÙŠÙˆÙ…
      const existingAttendance = await this.getAttendanceByDate(
        employeeId,
        today
      );

      if (existingAttendance) {
        // ØªØ­Ø¯ÙŠØ« ÙˆÙ‚Øª Ø§Ù„Ø¯Ø®ÙˆÙ„
        await this.updateAttendance(existingAttendance.id, {
          checkIn: Timestamp.now(),
          location,
        });
        return existingAttendance.id;
      } else {
        // Ø¥Ù†Ø´Ø§Ø¡ Ø³Ø¬Ù„ Ø¬Ø¯ÙŠØ¯
        const attendanceData: AttendanceWrite = {
          employeeId,
          ...withOwnerFields(ownerId),
          date: Timestamp.fromDate(today),
          checkIn: Timestamp.now(),
          breaks: [],
          regularHours: 0,
          overtimeHours: 0,
          totalHours: 0,
          status: 'present',
          location,
        };

        return await this.createAttendance(attendanceData);
      }
    } catch (error) {
      console.error('Error checking in:', error);
      throw error;
    }
  }

  // ØªØ³Ø¬ÙŠÙ„ Ø§Ù†ØµØ±Ø§Ù
  async checkOut(employeeId: string): Promise<void> {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const attendance = await this.getAttendanceByDate(employeeId, today);

      if (attendance && attendance.checkIn && !attendance.checkOut) {
        const checkOutTime = Timestamp.now();
        const workingHours = this.calculateWorkingHours(
          attendance.checkIn,
          checkOutTime,
          attendance.breaks
        );

        await this.updateAttendance(attendance.id, {
          checkOut: checkOutTime,
          totalHours: workingHours.total,
          regularHours: workingHours.regular,
          overtimeHours: workingHours.overtime,
        });
      }
    } catch (error) {
      console.error('Error checking out:', error);
      throw error;
    }
  }

  // Ø¥Ù†Ø´Ø§Ø¡ Ø³Ø¬Ù„ Ø­Ø¶ÙˆØ±
  private async createAttendance(attendanceData: AttendanceWrite): Promise<string> {
    try {
      const now = Timestamp.now();
      const docData = {
        ...attendanceData,
        createdAt: now,
        updatedAt: now,
      };

      const docRef = await addDoc(collection(db, this.collectionName), docData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating attendance:', error);
      throw error;
    }
  }

  // ØªØ­Ø¯ÙŠØ« Ø³Ø¬Ù„ Ø­Ø¶ÙˆØ±
  private async updateAttendance(
    attendanceId: string,
    updateData: Partial<Attendance>
  ): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, attendanceId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating attendance:', error);
      throw error;
    }
  }

  // Ø¬Ù„Ø¨ Ø­Ø¶ÙˆØ± Ù…ÙˆØ¸Ù ÙÙŠ ØªØ§Ø±ÙŠØ® Ù…Ø­Ø¯Ø¯
  async getAttendanceByDate(
    employeeId: string,
    date: Date,
    ownerId?: string
  ): Promise<Attendance | null> {
    try {
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);

      if (ownerId) {
        const ownerQuery = query(
          collection(db, this.collectionName),
          where('createdBy', '==', ownerId)
        );
        const ownerSnapshot = await getDocs(ownerQuery);
        const startMillis = startOfDay.getTime();
        const endMillis = endOfDay.getTime();
        const match = ownerSnapshot.docs
          .map(
            doc =>
              ({
                id: doc.id,
                ...doc.data(),
              } as Attendance)
          )
          .find(record => {
            const recordDate = record.date?.toMillis?.() || 0;
            return (
              record.employeeId === employeeId &&
              recordDate >= startMillis &&
              recordDate <= endMillis
            );
          });

        return match || null;
      }

      const q = query(
        collection(db, this.collectionName),
        where('employeeId', '==', employeeId),
        where('date', '>=', Timestamp.fromDate(startOfDay)),
        where('date', '<=', Timestamp.fromDate(endOfDay)),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
        } as Attendance;
      }
      return null;
    } catch (error) {
      console.error('Error getting attendance by date:', error);
      throw error;
    }
  }

  // Ø¬Ù„Ø¨ Ø­Ø¶ÙˆØ± Ù…ÙˆØ¸Ù Ù„ÙØªØ±Ø© Ù…Ø¹ÙŠÙ†Ø©
  async getAttendanceByPeriod(
    employeeId: string,
    startDate: Date,
    endDate: Date
  ): Promise<Attendance[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('employeeId', '==', employeeId),
        where('date', '>=', Timestamp.fromDate(startDate)),
        where('date', '<=', Timestamp.fromDate(endDate)),
        orderBy('date', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        doc =>
          ({
            id: doc.id,
            ...doc.data(),
          } as Attendance)
      );
    } catch (error) {
      console.error('Error getting attendance by period:', error);
      throw error;
    }
  }

  async getAttendanceForEmployees(
    employeeIds: string[],
    startDate: Date,
    endDate: Date,
    ownerId?: string
  ): Promise<Attendance[]> {
    if (!employeeIds.length) return [];

    if (ownerId) {
      const ownerSnapshot = await getDocs(
        query(
          collection(db, this.collectionName),
          where('createdBy', '==', ownerId)
        )
      );
      const employeeSet = new Set(employeeIds);
      const startMillis = startDate.getTime();
      const endMillis = endDate.getTime();

      return ownerSnapshot.docs
        .map(
          doc =>
            ({
              id: doc.id,
              ...doc.data(),
            } as Attendance)
        )
        .filter(record => {
          const recordDate = record.date?.toMillis?.() || 0;
          return (
            employeeSet.has(record.employeeId) &&
            recordDate >= startMillis &&
            recordDate <= endMillis
          );
        })
        .sort((a, b) => b.date.toMillis() - a.date.toMillis());
    }

    const chunks: string[][] = [];
    for (let index = 0; index < employeeIds.length; index += 10) {
      chunks.push(employeeIds.slice(index, index + 10));
    }

    const snapshots = await Promise.all(
      chunks.map(chunk =>
        getDocs(
          query(
            collection(db, this.collectionName),
            where('employeeId', 'in', chunk),
            where('date', '>=', Timestamp.fromDate(startDate)),
            where('date', '<=', Timestamp.fromDate(endDate)),
            orderBy('date', 'desc')
          )
        )
      )
    );

    return snapshots
      .flatMap(snapshot =>
        snapshot.docs.map(
          doc =>
            ({
              id: doc.id,
              ...doc.data(),
            } as Attendance)
        )
      )
      .sort((a, b) => b.date.toMillis() - a.date.toMillis());
  }

  async recordManualAttendance(
    attendanceData: Omit<AttendanceWrite, 'breaks' | 'regularHours' | 'overtimeHours' | 'totalHours'> &
      Partial<Pick<Attendance, 'breaks' | 'regularHours' | 'overtimeHours' | 'totalHours'>>
  ): Promise<string> {
    const employee = await employeesService.getEmployeeById(attendanceData.employeeId);
    const ownerId = getEmployeeOwner(employee);
    if (!employee || !ownerId) {
      throw new Error('تعذر تحديد مالك الموظف لتسجيل الحضور');
    }

    const existingAttendance = await this.getAttendanceByDate(
      attendanceData.employeeId,
      attendanceData.date.toDate(),
      ownerId
    );

    const workingHours =
      attendanceData.checkIn && attendanceData.checkOut
        ? this.calculateWorkingHours(
            attendanceData.checkIn,
            attendanceData.checkOut,
            attendanceData.breaks || []
          )
        : {
            total: attendanceData.totalHours || 0,
            regular: attendanceData.regularHours || 0,
            overtime: attendanceData.overtimeHours || 0,
          };

    const cleanData = removeUndefinedFields({
      ...attendanceData,
      ...withOwnerFields(ownerId),
      breaks: attendanceData.breaks || [],
      totalHours: workingHours.total,
      regularHours: workingHours.regular,
      overtimeHours: workingHours.overtime,
    });

    if (existingAttendance) {
      await this.updateAttendance(existingAttendance.id, cleanData);
      return existingAttendance.id;
    }

    return this.createAttendance(cleanData);
  }

  // Ø­Ø³Ø§Ø¨ Ø³Ø§Ø¹Ø§Øª Ø§Ù„Ø¹Ù…Ù„
  private calculateWorkingHours(
    checkIn: Timestamp,
    checkOut: Timestamp,
    breaks: Attendance['breaks']
  ) {
    const totalMinutes =
      (checkOut.toMillis() - checkIn.toMillis()) / (1000 * 60);

    // Ø®ØµÙ… Ø£ÙˆÙ‚Ø§Øª Ø§Ù„Ø§Ø³ØªØ±Ø§Ø­Ø©
    let breakMinutes = 0;
    breaks.forEach(breakItem => {
      if (breakItem.end) {
        breakMinutes +=
          (breakItem.end.toMillis() - breakItem.start.toMillis()) / (1000 * 60);
      }
    });

    const netMinutes = totalMinutes - breakMinutes;
    const netHours = netMinutes / 60;

    // Ø§Ù„Ø³Ø§Ø¹Ø§Øª Ø§Ù„Ù†Ø¸Ø§Ù…ÙŠØ© (8 Ø³Ø§Ø¹Ø§Øª)
    const regularHours = Math.min(netHours, 8);
    const overtimeHours = Math.max(netHours - 8, 0);

    return {
      total: netHours,
      regular: regularHours,
      overtime: overtimeHours,
    };
  }
}

// ======================== LEAVES SERVICE ========================
class LeavesService {
  private collectionName = 'leaves';

  // Ø¥Ù†Ø´Ø§Ø¡ Ø·Ù„Ø¨ Ø¥Ø¬Ø§Ø²Ø©
  async createLeave(
    leaveData: Omit<Leave, 'id' | 'createdAt' | 'updatedAt'> & EmployeeOwnership
  ): Promise<string> {
    try {
      const employee = await employeesService.getEmployeeById(leaveData.employeeId);
      const ownerId = leaveData.createdBy || getEmployeeOwner(employee);
      const now = Timestamp.now();
      const docData = {
        ...leaveData,
        ...withOwnerFields(ownerId),
        createdAt: now,
        updatedAt: now,
      };

      const docRef = await addDoc(collection(db, this.collectionName), docData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating leave:', error);
      throw error;
    }
  }

  // Ø¬Ù„Ø¨ Ø¥Ø¬Ø§Ø²Ø§Øª Ù…ÙˆØ¸Ù
  async getEmployeeLeaves(employeeId: string): Promise<Leave[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('employeeId', '==', employeeId),
        orderBy('requestDate', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        doc =>
          ({
            id: doc.id,
            ...doc.data(),
          } as Leave)
      );
    } catch (error) {
      console.error('Error getting employee leaves:', error);
      throw error;
    }
  }

  // Ø¬Ù„Ø¨ Ø·Ù„Ø¨Ø§Øª Ø§Ù„Ø¥Ø¬Ø§Ø²Ø§Øª Ø§Ù„Ù…Ø¹Ù„Ù‚Ø©
  async getLeavesByOwner(ownerId: string): Promise<Leave[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('createdBy', '==', ownerId),
        orderBy('requestDate', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        doc =>
          ({
            id: doc.id,
            ...doc.data(),
          } as Leave)
      );
    } catch (error) {
      console.error('Error getting organization leaves:', error);
      throw error;
    }
  }

  async getPendingLeaves(ownerId?: string): Promise<Leave[]> {
    try {
      const constraints: QueryConstraint[] = [
        where('status', '==', 'pending'),
        orderBy('requestDate', 'desc'),
      ];
      if (ownerId) {
        constraints.unshift(where('createdBy', '==', ownerId));
      }

      const q = query(
        collection(db, this.collectionName),
        ...constraints
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        doc =>
          ({
            id: doc.id,
            ...doc.data(),
          } as Leave)
      );
    } catch (error) {
      console.error('Error getting pending leaves:', error);
      throw error;
    }
  }

  // Ø§Ù„Ù…ÙˆØ§ÙÙ‚Ø© Ø¹Ù„Ù‰ Ø¥Ø¬Ø§Ø²Ø©
  async approveLeave(
    leaveId: string,
    approvedBy: string,
    notes?: string
  ): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, leaveId);
      await updateDoc(docRef, {
        status: 'approved',
        approvedBy,
        approvalDate: Timestamp.now(),
        approvalNotes: notes,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error approving leave:', error);
      throw error;
    }
  }

  // Ø±ÙØ¶ Ø¥Ø¬Ø§Ø²Ø©
  async rejectLeave(
    leaveId: string,
    approvedBy: string,
    notes?: string
  ): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, leaveId);
      await updateDoc(docRef, {
        status: 'rejected',
        approvedBy,
        approvalDate: Timestamp.now(),
        approvalNotes: notes,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error rejecting leave:', error);
      throw error;
    }
  }
}

// ======================== PAYROLL SERVICE ========================
class PayrollService {
  private collectionName = 'payrolls';

  // Ø­Ø³Ø§Ø¨ Ø§Ù„Ø±Ø§ØªØ¨ Ø§Ù„ØªÙ„Ù‚Ø§Ø¦ÙŠ
  async calculatePayroll(
    employeeId: string,
    month: number,
    year: number
  ): Promise<PayrollWrite> {
    try {
      // Ø¬Ù„Ø¨ Ø¨ÙŠØ§Ù†Ø§Øª Ø§Ù„Ù…ÙˆØ¸Ù
      const employee = await employeesService.getEmployeeById(employeeId);
      if (!employee) throw new Error('Employee not found');

      // ØªØ­Ø¯ÙŠØ¯ ÙØªØ±Ø© Ø§Ù„Ø±Ø§ØªØ¨
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0);

      // Ø¬Ù„Ø¨ Ø³Ø¬Ù„Ø§Øª Ø§Ù„Ø­Ø¶ÙˆØ± Ù„Ù„Ø´Ù‡Ø±
      const [attendanceRecords, leaveRecords] = await Promise.all([
        attendanceService.getAttendanceByPeriod(employeeId, startDate, endDate),
        leavesService.getEmployeeLeaves(employeeId),
      ]);

      // Ø­Ø³Ø§Ø¨ Ù…Ø¬Ù…ÙˆØ¹ Ø§Ù„Ø³Ø§Ø¹Ø§Øª ÙˆØ§Ù„Ø£ÙŠØ§Ù…
      const workingSummary = this.calculateWorkingSummary(
        attendanceRecords,
        leaveRecords,
        startDate,
        endDate
      );

      // Ø­Ø³Ø§Ø¨ Ø§Ù„Ø£Ø±Ø¨Ø§Ø­
      const earnings = this.calculateEarnings(employee, workingSummary);

      // Ø­Ø³Ø§Ø¨ Ø§Ù„Ø®ØµÙˆÙ…Ø§Øª
      const deductions = this.calculateDeductions(employee, earnings.total);

      return {
        employeeId,
        ...withOwnerFields(getEmployeeOwner(employee)),
        payPeriod: {
          month,
          year,
          startDate: Timestamp.fromDate(startDate),
          endDate: Timestamp.fromDate(endDate),
        },
        earnings,
        deductions,
        netSalary: earnings.total - deductions.total,
        workingHours: workingSummary.hours,
        attendance: workingSummary.attendance,
        status: 'calculated',
        calculatedBy: 'system', // ÙŠÙ…ÙƒÙ† ØªÙ…Ø±ÙŠØ± Ù…Ø¹Ø±Ù Ø§Ù„Ù…Ø³ØªØ®Ø¯Ù…
        calculatedAt: Timestamp.now(),
      };
    } catch (error) {
      console.error('Error calculating payroll:', error);
      throw error;
    }
  }

  // Ø¥Ù†Ø´Ø§Ø¡ ÙƒØ´Ù Ø±Ø§ØªØ¨
  async createPayroll(
    payrollData: PayrollWrite
  ): Promise<string> {
    try {
      const employee = await employeesService.getEmployeeById(payrollData.employeeId);
      const ownerId = payrollData.createdBy || getEmployeeOwner(employee);
      const now = Timestamp.now();
      const docData = {
        ...payrollData,
        ...withOwnerFields(ownerId),
        createdAt: now,
        updatedAt: now,
      };

      const docRef = await addDoc(collection(db, this.collectionName), docData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating payroll:', error);
      throw error;
    }
  }

  // Ø¬Ù„Ø¨ ÙƒØ´ÙˆÙ Ø±ÙˆØ§ØªØ¨ Ù…ÙˆØ¸Ù
  async getPayrollsByOwner(ownerId: string): Promise<Payroll[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('createdBy', '==', ownerId),
        orderBy('payPeriod.year', 'desc'),
        orderBy('payPeriod.month', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        doc =>
          ({
            id: doc.id,
            ...doc.data(),
          } as Payroll)
      );
    } catch (error) {
      console.error('Error getting organization payrolls:', error);
      throw error;
    }
  }

  async approvePayroll(payrollId: string, approvedBy: string): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, payrollId);
      await updateDoc(docRef, {
        status: 'approved',
        approvedBy,
        approvedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error approving payroll:', error);
      throw error;
    }
  }

  async markPayrollPaid(
    payrollId: string,
    paymentSource: 'cash' | 'bank' = 'cash'
  ): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, payrollId);
      const snapshot = await getDoc(docRef);
      if (!snapshot.exists()) {
        throw new Error('Payroll not found');
      }
      const payroll = snapshot.data() as Payroll;
      const teacherId = payroll.teacherId || payroll.tenantId || payroll.createdBy;
      if (!teacherId) {
        throw new Error('Payroll tenant was not found');
      }
      const callable = httpsCallable<
        {
          teacherId: string;
          payrollId: string;
          paymentSource: 'cash' | 'bank';
        },
        { success: boolean }
      >(functions, 'markPayrollPaidWithAccounting');
      await callable({ teacherId, payrollId, paymentSource });
    } catch (error) {
      console.error('Error marking payroll paid:', error);
      throw error;
    }
  }

  async getEmployeePayrolls(employeeId: string): Promise<Payroll[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('employeeId', '==', employeeId),
        orderBy('payPeriod.year', 'desc'),
        orderBy('payPeriod.month', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        doc =>
          ({
            id: doc.id,
            ...doc.data(),
          } as Payroll)
      );
    } catch (error) {
      console.error('Error getting employee payrolls:', error);
      throw error;
    }
  }

  // Ø­Ø³Ø§Ø¨ Ù…Ù„Ø®Øµ Ø§Ù„Ø¹Ù…Ù„
  private calculateWorkingSummary(
    attendanceRecords: Attendance[],
    leaveRecords: Leave[],
    startDate: Date,
    endDate: Date
  ) {
    const totalWorkingDays = this.getWorkingDaysInMonth(startDate, endDate);
    const leaveDays = leaveRecords
      .filter(leave => {
        if (leave.status !== 'approved') return false;
        const leaveStart = leave.startDate.toDate();
        const leaveEnd = leave.endDate.toDate();
        return leaveEnd >= startDate && leaveStart <= endDate;
      })
      .reduce((total, leave) => total + Math.max(leave.totalDays || 0, 0), 0);

    let regularHours = 0;
    let overtimeHours = 0;
    let workingDays = 0;
    let lateDays = 0;
    let absentDays = 0;

    attendanceRecords.forEach(record => {
      if (record.status === 'present') {
        workingDays++;
        regularHours += record.regularHours;
        overtimeHours += record.overtimeHours;

        // Ø§Ù„ØªØ­Ù‚Ù‚ Ù…Ù† Ø§Ù„ØªØ£Ø®ÙŠØ± (Ø¨Ø¹Ø¯ 9:00 ØµØ¨Ø§Ø­Ø§Ù‹)
        if (record.checkIn) {
          const checkInTime = record.checkIn.toDate();
          const expectedTime = new Date(checkInTime);
          expectedTime.setHours(9, 0, 0, 0);

          if (checkInTime > expectedTime) {
            lateDays++;
          }
        }
      } else if (record.status === 'absent') {
        absentDays++;
      }
    });

    // Ø­Ø³Ø§Ø¨ Ø£ÙŠØ§Ù… Ø§Ù„ØºÙŠØ§Ø¨ ØºÙŠØ± Ø§Ù„Ù…Ø³Ø¬Ù„Ø©
    const unrecordedAbsent = Math.max(
      totalWorkingDays - leaveDays - attendanceRecords.length,
      0
    );
    absentDays += unrecordedAbsent;

    return {
      hours: {
        regular: regularHours,
        overtime: overtimeHours,
        total: regularHours + overtimeHours,
      },
      attendance: {
        workingDays: totalWorkingDays,
        actualDays: workingDays,
        absentDays,
        lateDays,
        leaveDays,
      },
    };
  }

  // Ø­Ø³Ø§Ø¨ Ø§Ù„Ø£Ø±Ø¨Ø§Ø­
  private calculateEarnings(employee: Employee, workingSummary: any) {
    const basicSalary = employee.salary.base;
    let overtime = 0;
    let allowances = 0;
    let bonuses = 0;

    // Ø­Ø³Ø§Ø¨ Ø§Ù„Ø³Ø§Ø¹Ø§Øª Ø§Ù„Ø¥Ø¶Ø§ÙÙŠØ©
    if (employee.salary.overtimeRate && workingSummary.hours.overtime > 0) {
      overtime = workingSummary.hours.overtime * employee.salary.overtimeRate;
    }

    // Ø­Ø³Ø§Ø¨ Ø§Ù„Ø¨Ø¯Ù„Ø§Øª
    if (employee.salary.allowances) {
      const allowancesList = employee.salary.allowances;
      allowances =
        (allowancesList.transport || 0) +
        (allowancesList.housing || 0) +
        (allowancesList.food || 0) +
        (allowancesList.other || 0);
    }

    return {
      basicSalary,
      overtime,
      allowances,
      bonuses,
      commissions: 0,
      total: basicSalary + overtime + allowances + bonuses,
    };
  }

  // Ø­Ø³Ø§Ø¨ Ø§Ù„Ø®ØµÙˆÙ…Ø§Øª
  private calculateDeductions(employee: Employee, totalEarnings: number) {
    // Ø­Ø³Ø§Ø¨ Ø§Ù„Ø¶Ø±Ø§Ø¦Ø¨ ÙˆØ§Ù„ØªØ£Ù…ÙŠÙ†Ø§Øª (Ù†Ø³Ø¨ ØªÙ‚Ø¯ÙŠØ±ÙŠØ©)
    const tax = totalEarnings * 0.05; // 5%
    const insurance = totalEarnings * 0.02; // 2%
    const socialSecurity = totalEarnings * 0.09; // 9%

    return {
      tax,
      insurance,
      socialSecurity,
      loans: 0,
      advances: 0,
      penalties: 0,
      total: tax + insurance + socialSecurity,
    };
  }

  // Ø­Ø³Ø§Ø¨ Ø£ÙŠØ§Ù… Ø§Ù„Ø¹Ù…Ù„ ÙÙŠ Ø§Ù„Ø´Ù‡Ø± (Ø¨Ø¯ÙˆÙ† Ø§Ù„Ø¬Ù…Ø¹Ø© ÙˆØ§Ù„Ø³Ø¨Øª)
  private getWorkingDaysInMonth(startDate: Date, endDate: Date): number {
    let count = 0;
    const current = new Date(startDate);

    while (current <= endDate) {
      const dayOfWeek = current.getDay();
      // 0 = Ø§Ù„Ø£Ø­Ø¯ØŒ 6 = Ø§Ù„Ø³Ø¨ØªØŒ 5 = Ø§Ù„Ø¬Ù…Ø¹Ø©
      if (dayOfWeek !== 5 && dayOfWeek !== 6) {
        // ØªØ¬Ø§Ù‡Ù„ Ø§Ù„Ø¬Ù…Ø¹Ø© ÙˆØ§Ù„Ø³Ø¨Øª
        count++;
      }
      current.setDate(current.getDate() + 1);
    }

    return count;
  }
}

// ØªØµØ¯ÙŠØ± Ø§Ù„Ø®Ø¯Ù…Ø§Øª
export const employeesService = new EmployeesService();
export const attendanceService = new AttendanceService();
export const leavesService = new LeavesService();
export const payrollService = new PayrollService();

export const hrServices = {
  employees: employeesService,
  attendance: attendanceService,
  leaves: leavesService,
  payroll: payrollService,
};

export default hrServices;
